/* eslint-disable no-mixed-spaces-and-tabs */
// TODO - Type 'any' needs to be fixed.
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import EditIcon from "@mui/icons-material/Edit";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ReadMoreIcon from "@mui/icons-material/ReadMore";
import SendIcon from "@mui/icons-material/Send";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import TimerOffOutlinedIcon from "@mui/icons-material/TimerOffOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import { Button } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import format from "date-fns/format";
import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import isSameDay from "date-fns/isSameDay";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
	formatDate,
	getConfirmedWorkersCount,
	getTotalWorkersCount,
} from "../../../../../export/helpers";
import { ShiftBySkillsTag } from "../../../../../export/shift-scheduler";
import {
	FASTEST,
	JOB,
	SKILLS,
} from "../../../../assets/constants";
import { FLEX_SCHEDULER_PERMISSIONS } from "../../../../config/permissions";
import { isInterestAllocationType } from "../../../../utils/allocation-type";
import { formatDurationAbbreviated, hasPermissionString } from "../../../../utils/helpers";
import { getShiftIncompatibility } from "../../../../utils/shift-purpose-detail-field";
import {
	editDraftShiftDialogOpen,
	openReviewPublishShiftDialog,
} from "../../store/scheduler-slice";
import DeleteShift from "../delete-shift";

const BoxRow = ({ icon, content, fontWeight }: any) => {
	return (
		<Box display='flex' alignItems='center' pb={1}>
			{icon}
			<Box ml={1}>
				<div
					style={{
						whiteSpace: "normal",
						fontSize: "14px",
						fontWeight: `${fontWeight || 400}`,
					}}
				>
					{content}
				</div>
			</Box>
		</Box>
	);
};

export default function TooltipContent({
	shiftData,
	setShiftIdForShiftDetailsDialog,
	openPublishDraftShiftDialog,
	openShiftDetailsDialog,
	openShiftAttendanceDialog,
	toggleVisibility,
	openShiftCancelDialog,
	openDeleteDraftShiftDialog,
	visible,
	...restProps
}: any) {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const data = shiftData;
	const shiftByType = data.jobId === 0 ? SKILLS : JOB;
	const shiftAllocationType = shiftData.shiftAllocationType ?? FASTEST;
	const windowClosingTimeForSeniorityShiftAllocation =
		isInterestAllocationType(shiftAllocationType)
			? shiftData.windowClosingTime
			: null;
	// For switching between interested workers and confirmed workers
	const closedForInterest = windowClosingTimeForSeniorityShiftAllocation
		? new Date().getTime() >=
		  new Date(windowClosingTimeForSeniorityShiftAllocation).getTime()
		: false;
	const confirmedCountKey =
		isInterestAllocationType(shiftAllocationType) &&
		!closedForInterest
			? "workersInterested"
			: "slotsFilled";

	const confirmedCountValue = confirmedCountKey === "slotsFilled" 
		? getConfirmedWorkersCount(data) 
		: (data[confirmedCountKey] ?? "0");

	const shiftId = data?.id;
  const currentUserId = useSelector((state: any) => state.user?.userData?.id);
	const permissions = useSelector(
		(state: any) => state.user.userData.permissions
	);

	const shiftJob = useSelector((state: any) => {
		if (shiftByType === JOB) {
			// Shift by Job
			const jobRole =
				state.supervisor?.filterData?.allJobRoles.length > 0 &&
				state.supervisor?.filterData?.allJobRoles?.find(
					(jobRole: any) => String(jobRole.id) === String(data.jobId)
				);
			if (jobRole) return { name: jobRole.name };
			else return { name: "-" };
		} else {
			return { name: "-" };
		}
	});

	const shiftSkills = useSelector((state) => {
		if (shiftByType === SKILLS) {
			return data.skills ?? [];
		} else {
			return [];
		}
	});

	const workcenterName = useSelector((state: any) => {
		const workcenter = state.supervisor?.filterData?.allWorkcenters?.filter(
			(workcenter: any) => workcenter.id === data.workCenterId
		);
		if (workcenter.length > 0) {
			return workcenter[0].name;
		} else return t("scheduler_work-center");
	});

	const locationName = useSelector((state: any) => {
		const workcenter = state.supervisor?.filterData?.allWorkcenters?.find(
			(workcenter: any) => workcenter.id === data.workCenterId
		);
		if (workcenter && Object.prototype.hasOwnProperty.call(workcenter, "locationId")) {
			const location = state.supervisor?.filterData?.allLocations?.find(
				(location: any) => location.id === workcenter.locationId
			);
			if (location) {
				return location.name;
			} else return t("scheduler_location");
		}
	});

	const shiftPurposes = useSelector(
		(state: any) => state.supervisor?.filterData?.shiftReasons
	);
	const missingPurposeDetail = !data?.isPublished
		? getShiftIncompatibility(data, shiftPurposes)?.missingPurposeDetail
		: undefined;

	const isAfterShift = () => {
		return (
			isAfter(new Date(), new Date(data?.endDateTime)) ||
			isSameDay(new Date(), new Date(data?.startDateTime))
		);
	};

	const isBeforeShift = () => {
		return isBefore(new Date(), new Date(data?.startDateTime));
	};

	const draftDataContent = (
    <Box width="248px" zIndex={99999} pt={2}>
      {missingPurposeDetail && (
        <Box pb={1.5} pl={2.5} pr={2.5}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <ErrorOutlineOutlinedIcon
              sx={{ fontSize: "20px", color: "warning.main" }}
            />
            <Typography
              sx={{ fontSize: "14px", fontWeight: 500, color: "#202020" }}
            >
              {t("scheduler_purpose-details-missing")}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: "13px",
              lineHeight: "20px",
              color: "rgba(0,0,0,0.6)",
            }}
          >
            <Trans
              i18nKey="scheduler_purpose-details-missing-body"
              values={{
                field: missingPurposeDetail.label,
                purpose: missingPurposeDetail.purposeName,
              }}
              components={{
                strong: <strong style={{ color: "#202020", fontWeight: 500 }} />,
              }}
            />
          </Typography>
        </Box>
      )}
      {data.isUrgent && (
        <Box pb={1} pl={2.5} pr={2.5}>
          <Box
            sx={{
              padding: "2px 8px",
              display: "inline-flex",
              flexDirection: "row",
              alignItems: "center",
              border: 1,
              borderWidth: "1px",
              borderColor: "#D32F2F",
              borderRadius: "4px",
              backgroundColor: "#FFEBEE",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto",
                color: "#D32F2F",
                fontSize: "12px",
                fontWeight: "500",
                lineHeight: "20px",
                letterSpacing: "0.4px",
              }}
            >
              {t("scheduler_urgent")}
            </Typography>
            <ErrorOutlineIcon
              sx={{
                color: "#D32F2F",
                fontSize: "16px",
                marginLeft: "4px",
              }}
            />
          </Box>
        </Box>
      )}
      {shiftByType === JOB && (
        <Box pb={1} pl={2.5} pr={2.5}>
          <BoxRow
            icon={<WorkHistoryOutlinedIcon />}
            content={shiftJob.name ?? ""}
            fontWeight="500"
          />
        </Box>
      )}
      {shiftByType === SKILLS && (
        <Box pb={1} pl={2.5} pr={2.5}>
          <ShiftBySkillsTag skills={shiftSkills} />
        </Box>
      )}
      <Box pb={1} pl={2.5} pr={2.5}>
        <BoxRow
          icon={<AccessTimeOutlinedIcon />}
          content={
            <span>
              {formatDate({
                dateStr: data?.startDate,
                formatType: "TIME_RANGE",
                endDateStr: data?.endDate,
              })}
            </span>
          }
        />
      </Box>
      {locationName && (
        <Box pb={1} pl={2.5} pr={2.5}>
          <BoxRow icon={<LocationOnOutlinedIcon />} content={locationName} />
        </Box>
      )}
      {workcenterName && (
        <Box pb={1} pl={2.5} pr={2.5}>
          <BoxRow icon={<FactoryOutlinedIcon />} content={workcenterName} />
        </Box>
      )}
      <Divider sx={{ marginTop: "10px" }} />
      {isBeforeShift() && (
        <>
          <Button
            color="primary"
            size="large"
            variant="text"
            startIcon={<EditIcon />}
            onClick={(e: any) => {
              dispatch(editDraftShiftDialogOpen(data?.id.toString()));
              toggleVisibility(false);
            }}
            disabled={
              currentUserId?.toString() !== data?.createdBy?.toString()
                ? !hasPermissionString(
                    permissions,
                    FLEX_SCHEDULER_PERMISSIONS.EDIT_DRAFT_SHIFT
                  )
                : false
            }
            sx={{
              textTransform: "none",
              width: "100%",
              justifyContent: "flex-start",
              // Keep wrapped label lines left-aligned for longer FR/ES translations
              textAlign: "left",
              paddingLeft: "24px",
            }}
          >
            {t("scheduler_edit-shift")}
          </Button>
          <Divider />
        </>
      )}
      {isBeforeShift() && (
        <>
          <Button
            color="primary"
            size="large"
            variant="text"
            startIcon={<SendIcon />}
            onClick={() => {
              dispatch(openReviewPublishShiftDialog([shiftId]));
              toggleVisibility(false);
            }}
            disabled={!hasPermissionString(
              permissions,
              FLEX_SCHEDULER_PERMISSIONS.PUBLISH_SHIFT
            )}
            sx={{
              textTransform: "none",
              width: "100%",
              justifyContent: "flex-start",
              // Keep wrapped label lines left-aligned for longer FR/ES translations
              textAlign: "left",
              paddingLeft: "24px",
            }}
          >
            {t("scheduler_publish-shift")}
          </Button>
          <Divider />
        </>
      )}

      <Box>
        <DeleteShift
          shiftData={data}
          toggleVisibility={toggleVisibility}
          openDeleteDraftShiftDialog={() => {
            setShiftIdForShiftDetailsDialog(shiftId);
            openDeleteDraftShiftDialog();
          }}
          isDraftShift={true}
        />
      </Box>
    </Box>
  );

	const publishedDataContent = (
    <Box width="248px" zIndex={99999} pt={2}>
      {data.isUrgent && (
        <Box pb={1} pl={2.5} pr={2.5}>
          <Box
            sx={{
              padding: "2px 8px",
              display: "inline-flex",
              flexDirection: "row",
              alignItems: "center",
              border: 1,
              borderWidth: "1px",
              borderColor: "#D32F2F",
              borderRadius: "4px",
              backgroundColor: "#FFEBEE",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto",
                color: "#D32F2F",
                fontSize: "12px",
                fontWeight: "500",
                lineHeight: "20px",
                letterSpacing: "0.4px",
              }}
            >
              {t("scheduler_urgent")}
            </Typography>
            <ErrorOutlineIcon
              sx={{
                color: "#D32F2F",
                fontSize: "16px",
                marginLeft: "4px",
              }}
            />
          </Box>
        </Box>
      )}
      {shiftByType === JOB && (
        <Box pb={1} pl={2.5} pr={2.5}>
          <BoxRow
            icon={<WorkHistoryOutlinedIcon />}
            content={shiftJob.name ?? ""}
            fontWeight="500"
          />
        </Box>
      )}
      {shiftByType === SKILLS && (
        <Box pb={1} pl={2.5} pr={2.5}>
          <ShiftBySkillsTag skills={shiftSkills} />
        </Box>
      )}
      <Box pb={1} pl={2.5} pr={2.5}>
        <BoxRow
          icon={<AccessTimeOutlinedIcon />}
          content={
            <span>
              {formatDate({
                dateStr: data?.startDate,
                formatType: "TIME_RANGE",
                endDateStr: data?.endDate,
              })}
            </span>
          }
        />
      </Box>
      <Box pb={1} pl={2.5} pr={2.5}>
        <BoxRow icon={<LocationOnOutlinedIcon />} content={locationName} />
      </Box>
      <Box pb={1} pl={2.5} pr={2.5}>
        <BoxRow icon={<FactoryOutlinedIcon />} content={workcenterName} />
      </Box>
      {isInterestAllocationType(shiftAllocationType) && (
        <Box pb={1} pl={2.5} pr={2.5}>
          <BoxRow
            icon={
              closedForInterest ? (
                <TimerOffOutlinedIcon />
              ) : (
                <TimerOutlinedIcon />
              )
            }
            content={`${
              new Date(windowClosingTimeForSeniorityShiftAllocation).getDate() -
                new Date().getDate() ===
              1
                ? `${t("scheduler_tomorrow")} ${format(
                    new Date(windowClosingTimeForSeniorityShiftAllocation),
                    "h:mm a"
                  )}`
                : new Date(
                    windowClosingTimeForSeniorityShiftAllocation
                  ).getDate() -
                    new Date().getDate() ===
                  0
                ? `${t("scheduler_today")} ${format(
                    new Date(windowClosingTimeForSeniorityShiftAllocation),
                    "h:mm a"
                  )}`
                : new Date(
                    windowClosingTimeForSeniorityShiftAllocation
                  ).getDate() -
                    new Date().getDate() ===
                  -1
                ? `${t("scheduler_yesterday")} ${format(
                    new Date(windowClosingTimeForSeniorityShiftAllocation),
                    "h:mm a"
                  )}`
                : formatDate({
                    dateStr: windowClosingTimeForSeniorityShiftAllocation,
                    formatType: "DATE_WITH_TIME",
                  })
            } ${closedForInterest ? `(${t("scheduler_window-closed")})` : ""}`}
          />
        </Box>
      )}
      {/* <Box pb={1} pl={2.5} pr={2.5}>
        <BoxRow
          icon={<RemoveRedEyeOutlinedIcon />}
          content={
            <>
              {" "}
              {data?.shiftViews ?? 0}/
              {getTotalWorkersCount(parseInt(data?.shiftViews ?? "0"), parseInt(data?.workersNotified ?? "0"))} Viewed
            </>
          }
        />
      </Box> */}
      <Box pb={1} pl={2.5} pr={2.5}>
        <BoxRow
          icon={<ThumbUpOutlinedIcon />}
          content={(() => {
            const needed = parseInt(data?.numberOfWorkersNeeded) || 0;
            const isFlexi = data?.isFlexiSignupEnabled;
            const isPartial = data?.isPartialSignupEnabled;

            if (isFlexi || isPartial) {
              const shiftStart = new Date(data?.startDate || data?.startDateTime);
              const shiftEnd = new Date(data?.endDate || data?.endDateTime);
              const shiftDurationHours = (shiftEnd.getTime() - shiftStart.getTime()) / (1000 * 60 * 60);
              const totalHoursRequiredRaw = needed * shiftDurationHours;
              const coveredHoursRaw = (data?.slotsFilled || 0) * shiftDurationHours;

              if (coveredHoursRaw === 0) {
                return `${t("scheduler_open")} • ${formatDurationAbbreviated(totalHoursRequiredRaw)}`;
              } else {
                const percentage = needed > 0 ? Math.round(((data?.slotsFilled || 0) / needed) * 100) : 0;
                return `${percentage}% • ${formatDurationAbbreviated(coveredHoursRaw)}/${formatDurationAbbreviated(totalHoursRequiredRaw)}`;
              }
            } else {
              const signedUp = confirmedCountValue;
              const isApplied = isInterestAllocationType(shiftAllocationType) &&
              !closedForInterest;

              if (signedUp === 0 || signedUp === "0") {
                return `${t("scheduler_open")} • ${needed} ${needed > 1 ? t("scheduler_workers") : t("scheduler_worker")}`;
              } else {
                return `${signedUp}/${needed} ${needed > 1 ? t("scheduler_workers") : t("scheduler_worker")} ${isApplied ? t("scheduler_applied") : t("scheduler_confirmed")}`;
              }
            }
          })()}
        />
      </Box>
      <Divider sx={{ marginTop: "10px" }} />
      {hasPermissionString(
        permissions,
        FLEX_SCHEDULER_PERMISSIONS.VIEW_SHIFT_DETAILS
      ) && (
        <Box>
          <Button
            color="primary"
            size="large"
            variant="text"
            startIcon={<ReadMoreIcon />}
            onClick={() => {
              setShiftIdForShiftDetailsDialog(shiftId);
              openShiftDetailsDialog();
              toggleVisibility(false);
            }}
            sx={{
              textTransform: "none",
              width: "100%",
              justifyContent: "flex-start",
              // Keep wrapped label lines left-aligned for longer FR/ES translations
              textAlign: "left",
              paddingLeft: "24px",
            }}
          >
            {t("scheduler_view-details")}
          </Button>
        </Box>
      )}
      {isAfterShift() && data?.slotsFilled ? (
        <>
          <Divider />
          <Button
            color="primary"
            size="large"
            variant="text"
            startIcon={<FactCheckIcon />}
            sx={{
              textTransform: "none",
              width: "100%",
              justifyContent: "flex-start",
              // Keep wrapped label lines left-aligned for longer FR/ES translations
              textAlign: "left",
              paddingLeft: "24px",
            }}
            onClick={() => {
              setShiftIdForShiftDetailsDialog(shiftId);
              openShiftAttendanceDialog();
              toggleVisibility(false);
            }}
            disabled={!hasPermissionString(
              permissions,
              FLEX_SCHEDULER_PERMISSIONS.MARK_ATTENDANCE
            )}
          >
            {t("scheduler_mark-attendance")}
          </Button>
        </>
      ) : null}
      {isBeforeShift() && (
        <>
          <Divider />
          <Button
            color="primary"
            size="large"
            variant="text"
            startIcon={<EditIcon />}
            onClick={(e: any) => {
              dispatch(editDraftShiftDialogOpen(data?.id.toString()));
              toggleVisibility(false);
            }}
            sx={{
              textTransform: "none",
              width: "100%",
              justifyContent: "flex-start",
              // Keep wrapped label lines left-aligned for longer FR/ES translations
              textAlign: "left",
              paddingLeft: "24px",
            }}
            disabled={!hasPermissionString(
              permissions,
              FLEX_SCHEDULER_PERMISSIONS.EDIT_PUBLISHED_SHIFT
            )}
          >
            {t("scheduler_edit-shift")}
          </Button>
        </>
      )}
      <Box>
        <Divider />
        <DeleteShift
          shiftData={data}
          toggleVisibility={toggleVisibility}
          openShiftCancelDialog={() => {
            setShiftIdForShiftDetailsDialog(shiftId);
            openShiftCancelDialog();
          }}
        />
      </Box>
    </Box>
  );

	// For draft shifts
	if (!data?.isPublished) {
		return draftDataContent;
	} // For published shifts
	else return publishedDataContent;
}
